import { BADGE_SIZE } from './_storeBadges'

// Store links for the footer badges (_Layout) and the in-page app promos
// (_AppPromo), localized to the visitor's site locale.
//
// Both bases are deliberately COUNTRY-LESS. The App Store URL used to be pinned
// to /us/, which does not redirect — every non-US visitor landed on the US
// storefront regardless of where they were. Dropping the segment lets Apple
// geo-resolve the storefront, which is also what picks the listing language:
// a visitor in Germany gets the German storefront in German with no parameter
// from us at all. Google Play has never needed a country segment.
const APP_STORE_BASE = 'https://apps.apple.com/app/id585223877'
const PLAY_STORE_BASE = 'https://play.google.com/store/apps/details?id=me.tidesnear.free'

// Download attribution, so the stores report installs driven by the site —
// Plausible sees the click, these see what happened after it.
//
// Apple: App Store Connect campaign parameters (Analytics → Acquisition →
// Campaigns). pt is the account's provider ID, ct the campaign registered
// there ("website"), mt=8 means "app". Per-placement breakdown stays
// Plausible's job — Apple wants every ct pre-registered, so one campaign.
//
// Play: the referrer param carries UTM tags into Play Console's acquisition
// reporting (and the Install Referrer API, should the Android app ever want
// to know an install came from the site). Value is URL-encoded by hand so
// the file stays grep-able for the plain string below.
const APP_STORE_CAMPAIGN = 'pt=242921&ct=website&mt=8'
// utm_source=tidesnear.me&utm_medium=web&utm_campaign=website
const PLAY_REFERRER =
  'referrer=utm_source%3Dtidesnear.me%26utm_medium%3Dweb%26utm_campaign%3Dwebsite'

// Apple's ?l= only offers languages the RESOLVED STOREFRONT actually carries.
// Verified against the live listing: on the US storefront l=es serves es-MX and
// l=ja is ignored (en-US), because the US store does not offer Japanese. That
// is the correct fallback rather than a bug — a Japanese-locale visitor in the
// US would see English on the store anyway.
//
// So this parameter is not what localizes the common case (the storefront is).
// It earns its place for the MINORITY-LANGUAGE visitor: someone reading the
// site in Spanish from a US IP resolves to the US storefront, where l=es is the
// difference between the Spanish and English listing.
//
// English is absent on purpose — no parameter means "storefront default".
const APPLE_LANG = {
  'en-GB': 'en-GB',
  es: 'es',
  'es-ES': 'es',
  'es-419': 'es-MX',
  pt: 'pt-PT',
  'pt-PT': 'pt-PT',
  'pt-BR': 'pt-BR',
  fr: 'fr',
  de: 'de',
  it: 'it',
  nl: 'nl',
  da: 'da',
  nb: 'nb',
  ja: 'ja',
  ko: 'ko',
  'zh-Hans': 'zh-Hans',
  'zh-Hant': 'zh-Hant',
}

// Play's hl= is not storefront-limited — every code below was verified against
// the live listing, which echoed each one back in <html lang>. Note nb: Play
// normalises it to "no", so passing our own code through is fine.
const PLAY_LANG = {
  en: 'en',
  'en-GB': 'en-GB',
  es: 'es',
  'es-ES': 'es',
  'es-419': 'es-419',
  pt: 'pt-PT',
  'pt-PT': 'pt-PT',
  'pt-BR': 'pt-BR',
  fr: 'fr',
  de: 'de',
  it: 'it',
  nl: 'nl',
  da: 'da',
  nb: 'nb',
  ja: 'ja',
  ko: 'ko',
  'zh-Hans': 'zh-CN',
  'zh-Hant': 'zh-TW',
}

export function appStoreUrl(locale) {
  const l = APPLE_LANG[locale]
  return `${APP_STORE_BASE}?${l ? `l=${l}&` : ''}${APP_STORE_CAMPAIGN}`
}

export function playStoreUrl(locale) {
  const hl = PLAY_LANG[locale] || 'en'
  return `${PLAY_STORE_BASE}&${PLAY_REFERRER}&hl=${hl}`
}

export const storeUrl = (store, locale) =>
  store === 'ios' ? appStoreUrl(locale) : playStoreUrl(locale)

// --- Badge artwork -------------------------------------------------------
//
// Both stores require the localized badge on a localized page, so these map a
// site locale onto the artwork fetched by scripts/fetch-store-badges.py. The
// two maps differ in more than spelling, which is why they are not one table:
//
//   nb  — Apple publishes no Norwegian badge (its API answers with the US-UK
//         artwork), so Norwegian reads English on Apple and Norwegian on Play.
//   pt-PT — the reverse: Apple has a Portugal-specific badge, Google does not,
//         so European Portuguese falls back to the Brazilian badge on Play.
//
// Anything unmapped falls back to English, which is wrong-language but never a
// broken image.
const APPLE_BADGE = {
  en: 'en',
  'en-GB': 'en',
  nb: 'en',
  es: 'es-es',
  'es-ES': 'es-es',
  'es-419': 'es-mx',
  pt: 'pt-pt',
  'pt-PT': 'pt-pt',
  'pt-BR': 'pt-br',
  fr: 'fr',
  de: 'de',
  it: 'it',
  nl: 'nl',
  da: 'da',
  ja: 'ja',
  ko: 'ko',
  'zh-Hans': 'zh-cn',
  'zh-Hant': 'zh-tw',
}

const PLAY_BADGE = {
  en: 'en',
  'en-GB': 'en',
  es: 'es',
  'es-ES': 'es',
  'es-419': 'es-419',
  pt: 'pt-br',
  'pt-PT': 'pt-br',
  'pt-BR': 'pt-br',
  fr: 'fr',
  de: 'de',
  it: 'it',
  nl: 'nl',
  da: 'da',
  nb: 'no',
  ja: 'ja',
  ko: 'ko',
  'zh-Hans': 'zh-cn',
  'zh-Hant': 'zh-tw',
}

// Resolve the badge for a locale at a given rendered height, returning the
// explicit width alongside it. Callers must set both on the <img>: the badges
// are not a common shape (Korean is 129.7 units wide against Japanese at
// 108.85), so height alone leaves the box with no width until the file loads
// and the badge snaps from zero to full width as it arrives.
export function storeBadge(store, locale, height) {
  const ios = store === 'ios'
  const key = (ios ? APPLE_BADGE : PLAY_BADGE)[locale] || 'en'
  const [w, h] = BADGE_SIZE[ios ? 'apple' : 'play'][key]
  return {
    src: ios ? `/app-store-badge-${key}.svg` : `/google-play-badge-${key}.png`,
    width: Math.round(((height * w) / h) * 10) / 10,
    height,
  }
}

// Which store a visitor is being sent to, and from where. Plausible's script is
// production-only (see the layout), so in dev this is a no-op — the optional
// call is what makes that safe rather than a ReferenceError on every click.
//
// The queue stub in the layout head means clicks that happen before
// plausible.js finishes loading are replayed rather than dropped, which matters
// here: the promo is above the fold on mobile.
export function trackStoreClick(store, placement) {
  try {
    window.plausible?.('Store Click', { props: { store, placement } })
  } catch {
    // Analytics must never break a link the user just clicked.
  }
}
